/**
 * <s-product-gallery> — One-image-at-a-time product gallery with previous/next arrows.
 *
 * Arrow buttons use the Invoker Commands API: commandfor points at this element's id
 * and command is `--previous` or `--next`. Native scroll-snap still owns swiping.
 *
 * Required markup:
 *   [data-product-gallery]         — horizontal scroll-snap track holding one slide per media.
 *   [data-gallery-prev]            — previous button (commandfor=<this id>, command="--previous").
 *   [data-gallery-next]            — next button (commandfor=<this id>, command="--next").
 *   [data-gallery-count]           — optional "current / total" text.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command === '--next') this.#go(1);
      if (event.command === '--previous') this.#go(-1);
    }, opts);

    this.#track?.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#updateControls());
    }, { ...opts, passive: true });

    window.addEventListener('resize', () => this.#updateControls(), opts);
    this.#updateControls();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #track() {
    return this.querySelector('[data-product-gallery]');
  }

  // Slides in visual order; variant selection can move one first via CSS order.
  #slides() {
    const track = this.#track;
    if (!track) return [];
    return Array.from(track.children)
      .filter((slide) => slide.offsetWidth > 0)
      .sort((a, b) => a.offsetLeft - b.offsetLeft);
  }

  #currentIndex() {
    const track = this.#track;
    const slides = this.#slides();
    if (!track || !slides.length) return 0;

    const start = track.getBoundingClientRect().left;
    let best = 0;
    let bestDistance = Infinity;
    slides.forEach((slide, index) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - start);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  #go(step) {
    const track = this.#track;
    const slides = this.#slides();
    if (!track || !slides.length) return;

    const index = Math.min(Math.max(this.#currentIndex() + step, 0), slides.length - 1);
    const offset = slides[index].getBoundingClientRect().left - track.getBoundingClientRect().left;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: offset, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  #updateControls() {
    const slides = this.#slides();
    const index = this.#currentIndex();
    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    const count = this.querySelector('[data-gallery-count]');

    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= slides.length - 1;
    if (count) count.textContent = `${index + 1} / ${slides.length}`;
  }
}
customElements.define('s-product-gallery', SProductGallery);
